import React, { useState } from 'react';
import { FormContainer } from '../../../styled';
import { fetchApiData } from '../../../Utils/ApiCalls';
import { FETCH_PLATFORM_ACCESS, UPDATE_PLATFORM_ACCESS } from '../../../urls';
import { Button, Input, RadioGroup, showToast, Typography } from '@attentive-platform/stem-ui';

const USER_TYPE = {
  PRO: 'pro',
  ENTERPRISE: 'enterprise'
};

const UserTypeSelector = ({ userType, onUserTypeChange }) => {
  return (
    <div className='flex items-center justify-between gap-4'>
      <Typography variant='body3' className='font-medium block'>
        User type
      </Typography>
      <RadioGroup
        value={userType}
        onChange={(event, newValue) => onUserTypeChange(newValue)}
        radioGroup={[
          { label: 'Pro', value: USER_TYPE.PRO },
          { label: 'Enterprise', value: USER_TYPE.ENTERPRISE }
        ]}
        row
      />
    </div>
  );
};

const EmailInputSection = ({ email, onEmailChange, onProceed, loading, isEmailDisabled, buttonText }) => {
  return (
      <div className='flex gap-2 border-none'>
        <Input
          label='Email'
          type='email'
          value={email}
          fullWidth
          placeholder='Enter email'
          disabled={isEmailDisabled}
          onChange={e => onEmailChange('email', e.target.value)}
        />
        <Button color='primary' variant='outlined' onClick={onProceed} loading={loading} className='ml-2 text-base'>
        {buttonText}
      </Button>
    </div>
  );
};

const DateInputsSection = ({ formData, onDataChange, userType }) => {
  const fieldConfigs = {
    [USER_TYPE.ENTERPRISE]: [
      {
        key: 'planStartDate',
        label: 'Contract start date',
        disabled: true
      },
      {
        key: 'planEndDate',
        label: 'Contract end date',
        disabled: !formData.planStartDate
      }
    ],
    [USER_TYPE.PRO]: [
      {
        key: 'planStartDate',
        label: 'Plan start date',
        disabled: true
      },
      {
        key: 'nextBillingDate',
        label: 'Next billing date',
        disabled: true
      },
      {
        key: 'planEndDate',
        label: 'Plan end date',
        disabled: !formData.planStartDate && !formData.nextBillingDate
      }
    ]
  };

  const DateInput = ({ config }) => (
    <div className='flex items-center justify-between'>
        <Typography variant='body3' className='font-medium'>
        {config.label}
      </Typography>
      <input
        type='date'
        className='px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-48'
        disabled={config.disabled}
        value={formData[config.key] || ''}
        onChange={e => onDataChange(config.key, e.target.value)}
      />
    </div>
  );

  const currentConfig = fieldConfigs[userType];

  return (
    <div className='space-y-4 mb-2'>
      {currentConfig.map((config, index) => (
        <DateInput key={index} config={config} />
      ))}
    </div>
  );
};

const EnablePlatformAccess = () => {
  const [formData, setFormData] = useState({
    email: '',
    planEndDate: '',
    planStartDate: '',
    nextBillingDate: '',
    userType: USER_TYPE.PRO
  });
  const [loading, setLoading] = useState(false);
  const [accessId, setAccessId] = useState(null);
  const [proceedLoading, setProceedLoading] = useState(false);

  const handleDataChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleUserTypeChange = type => {
    setFormData(prev => ({
      ...prev,
      email: '',
      userType: type,
      planEndDate: '',
      planStartDate: '',
      nextBillingDate: ''
    }));
    setAccessId(null);
  };

  const handleProceed = async () => {
    if (!formData.email) {
      showToast('Please enter an email address', 'error');
      return;
    }

    setProceedLoading(true);

    const data = {
      email: formData.email,
      is_pro: formData.userType === USER_TYPE.PRO
    };

    try {
      const response = await fetchApiData({
        endpoint: FETCH_PLATFORM_ACCESS,
        apiRequest: 'POST',
        data
      });

      if (response.error) {
        showToast(response.error || 'Failed to fetch platform access data', 'error');
        return;
      }

      const { id, start_date, next_billing_date } = response?.data;

      setAccessId(id);
      setFormData(prev => ({
        ...prev,
        planStartDate: start_date || '',
        nextBillingDate: next_billing_date || '',
        planEndDate: ''
      }));

      showToast('Platform access data fetched successfully', 'success');
    } catch (error) {
      showToast('Failed to fetch platform access data', 'error');
    } finally {
      setProceedLoading(false);
    }
  };

  const handleClear = () => {
    setFormData(prev => ({
      ...prev,
      email: '',
      planEndDate: '',
      planStartDate: '',
      nextBillingDate: ''
    }));
    setAccessId(null);
    showToast('Form cleared successfully', 'success');
  };

  const handleUpdate = async () => {
    if (!formData.planEndDate) {
      showToast('Please fill in the end date', 'error');
      return;
    }

    setLoading(true);

    try {
      const response = await fetchApiData({
        endpoint: UPDATE_PLATFORM_ACCESS,
        apiRequest: 'POST',
        data: {
          id: accessId,
          email: formData.email,
          admin_email: formData.email,
          end_date: formData.planEndDate,
          is_pro: formData.userType === USER_TYPE.PRO
        }
      });

      if (response.error) {
        showToast(response.error || 'Failed to update platform access', 'error');
        return;
      }

      showToast('Platform access update initiated successfully', 'success');

      setFormData({
        email: '',
        planEndDate: '',
        planStartDate: '',
        nextBillingDate: '',
        userType: USER_TYPE.PRO
      });
      setAccessId(null);
    } catch (error) {
      showToast('Failed to update platform access', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isEnterprise = formData.userType === USER_TYPE.ENTERPRISE;
  const isUpdateDisabled = isEnterprise
    ? !formData.planStartDate || !formData.planEndDate || !accessId
    : !formData.planStartDate || !formData.nextBillingDate || !formData.planEndDate || !accessId;

  const isEmailDisabled = !!accessId;
  const buttonText = accessId ? 'Clear' : 'Proceed';
  const handleEmailAction = accessId ? handleClear : handleProceed;

  return (
    <FormContainer>
      <UserTypeSelector userType={formData.userType} onUserTypeChange={handleUserTypeChange} />

      <EmailInputSection
        email={formData.email}
        buttonText={buttonText}
        loading={proceedLoading}
        onProceed={handleEmailAction}
        onEmailChange={handleDataChange}
        isEmailDisabled={isEmailDisabled}
      />

      <DateInputsSection formData={formData} userType={formData.userType} onDataChange={handleDataChange} />

      <div className='flex justify-end'>
        <Button
          color='primary'
          size='medium'
          variant='contained'
          onClick={handleUpdate}
          disabled={isUpdateDisabled}
          loading={loading}
          className='text-base'>
          Update
        </Button>
      </div>
    </FormContainer>
  );
};

export default EnablePlatformAccess;
